// @tennis-matches/services/appearance
// Best-effort Laden/Speichern von UI-Theme-Overrides (Whitelist-basiert).
// Reads via public.get_ui_appearance_theme (app_config oft nicht PostgREST-exposed → 406).

import { getSupabaseClient, dbRepository } from "../supabase/index.js";
import {
  APP_CONFIG_SCHEMA_REST_KEY,
  isAppConfigSchemaUnavailableError,
  isRestEndpointUnavailable,
  markRestEndpointUnavailable,
} from "../supabase/rest-endpoint-availability.js";
import {
  APPEARANCE_TOKENS_V1,
  APPEARANCE_TOKENS_BY_KEY,
  sanitizeAppearanceOverrides,
} from "./definitions.js";

const SCHEMA = "app_config";
const T_SETTINGS = "ui_settings";
const KEY_THEME = "appearance.theme.v1";
const RPC_GET_THEME = "get_ui_appearance_theme";
const REST_KEY_RPC_GET = `rpc:${RPC_GET_THEME}`;

const __LS_PREFIX = "ttb:appearanceTheme:";
const __LS_VERSION = 1;

function __asStr(v) {
  return String(v ?? "").trim();
}
function __db(supabase) {
  return supabase && typeof supabase.schema === "function"
    ? supabase.schema(SCHEMA)
    : supabase;
}

function __isMissingTableError(error) {
  const code = __asStr(error?.code);
  const status = Number(error?.status || 0);
  const msg = __asStr(error?.message).toLowerCase();
  return (
    status === 404 ||
    status === 406 ||
    code === "42P01" ||
    code === "42703" ||
    code === "PGRST116" ||
    code === "PGRST202" ||
    code === "42883" ||
    msg.includes("not acceptable") ||
    msg.includes("json object requested") ||
    msg.includes("could not find the function")
  );
}

function __firstRow(data) {
  if (Array.isArray(data)) return data[0] || null;
  return data && typeof data === "object" ? data : null;
}

function __themeValueFromRpc(data) {
  if (data && typeof data === "object" && !Array.isArray(data)) return data;
  return {};
}

function __lsKey(scope) {
  const s = __asStr(scope || "global") || "global";
  return `${__LS_PREFIX}${s}`;
}

function __readLs(scope) {
  try {
    const raw = localStorage.getItem(__lsKey(scope));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (parsed.v !== __LS_VERSION) return null;
    const value = parsed.value;
    if (!value || typeof value !== "object") return null;
    return value;
  } catch (_) {
    return null;
  }
}

function __writeLs(scope, value) {
  try {
    if (!value || typeof value !== "object") return;
    localStorage.setItem(
      __lsKey(scope),
      JSON.stringify({ v: __LS_VERSION, ts: Date.now(), value }),
    );
  } catch (_) {}
}

function __getIdentifier() {
  const key = "tm_settings_identifier";
  let id = null;
  try {
    id = localStorage.getItem(key);
  } catch (_) {}
  if (!id) {
    id = `device_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    try {
      localStorage.setItem(key, id);
    } catch (_) {}
  }
  return String(id || "").trim();
}

function __getCtxHints() {
  const tenantId = dbRepository?.getContext?.().tenantId || null;
  return { tenantId: tenantId ? String(tenantId) : null };
}

function __scopeIdFor({ scope, tenant_id, user_id, identifier: ident } = {}) {
  return String(scope || "global") === "global"
    ? "global"
    : String(scope || "") === "tenant"
      ? String(tenant_id || "")
      : user_id
        ? String(user_id)
        : String(ident || "");
}

/**
 * Applies a sanitized appearance theme map (key -> value) onto :root as CSS vars.
 * @param {Record<string,string>} themeValues
 */
export function applyAppearanceTheme(themeValues) {
  const vals =
    themeValues && typeof themeValues === "object" ? themeValues : null;
  if (!vals) return;
  const root = document?.documentElement;
  if (!root) return;

  for (const [k, v] of Object.entries(vals)) {
    const def = APPEARANCE_TOKENS_BY_KEY[k];
    if (!def) continue;
    try {
      root.style.setProperty(def.cssVar, String(v));
    } catch (_) {}
  }
}

export function clearAppearanceTheme() {
  const root = document?.documentElement;
  if (!root) return;
  for (const def of APPEARANCE_TOKENS_V1) {
    try {
      root.style.removeProperty(def.cssVar);
    } catch (_) {}
  }
}

export function applyCachedAppearanceTheme() {
  const cached = __readLs("global");
  if (!cached) return false;
  const res = sanitizeAppearanceOverrides(cached);
  if (!res.ok) return false;
  applyAppearanceTheme(res.values);
  return true;
}

/**
 * Loads theme overrides with best-effort fallback:
 * - localStorage cache (global)
 * - public RPC get_ui_appearance_theme (kein app_config REST)
 */
export async function loadAppearanceThemeRemote() {
  const cached = __readLs("global");
  if (cached) {
    const s = sanitizeAppearanceOverrides(cached);
    if (s.ok)
      return { ok: true, theme: s.values, cached: true, warnings: s.warnings };
  }

  if (isRestEndpointUnavailable(REST_KEY_RPC_GET)) {
    return { ok: true, theme: {}, cached: false, missing: true };
  }

  const supabase = await getSupabaseClient();
  if (!supabase)
    return { ok: true, theme: {}, cached: false, reason: "no-supabase" };

  const { tenantId } = __getCtxHints();
  const identifier = __getIdentifier();
  const merged = {};
  /** @type {Record<string,string>} */
  const warnings = {};

  const fetchScope = async ({
    scope,
    tenant_id,
    user_id,
    identifier: ident,
  } = {}) => {
    const scopeId = __scopeIdFor({
      scope,
      tenant_id,
      user_id,
      identifier: ident,
    });
    const { data, error } = await supabase.rpc(RPC_GET_THEME, {
      p_scope: String(scope || "global"),
      p_scope_id: scopeId,
    });
    if (error) return { data: null, error };
    return { data: { value: __themeValueFromRpc(data) }, error: null };
  };

  let userId = null;
  try {
    const { data } = await supabase.auth.getUser();
    userId = data?.user?.id || null;
  } catch (_) {}

  try {
    const rGlobal = await fetchScope({ scope: "global" });
    if (rGlobal?.error) {
      if (__isMissingTableError(rGlobal.error)) {
        markRestEndpointUnavailable(REST_KEY_RPC_GET);
        return { ok: true, theme: {}, cached: false, missing: true };
      }
      return { ok: false, reason: "db-error", error: rGlobal.error };
    }
    const globalValue =
      rGlobal?.data?.value && typeof rGlobal.data.value === "object"
        ? rGlobal.data.value
        : {};
    const sG = sanitizeAppearanceOverrides(globalValue);
    if (sG.ok) Object.assign(merged, sG.values);
    Object.assign(warnings, sG.ok ? sG.warnings : {});

    const rTenant = tenantId
      ? await fetchScope({ scope: "tenant", tenant_id: tenantId })
      : { data: null, error: null };
    if (rTenant?.error) {
      if (!__isMissingTableError(rTenant.error))
        console.warn("[appearance] tenant fetch failed:", rTenant.error);
    } else {
      const tenantValue =
        rTenant?.data?.value && typeof rTenant.data.value === "object"
          ? rTenant.data.value
          : {};
      const sT = sanitizeAppearanceOverrides(tenantValue);
      if (sT.ok) Object.assign(merged, sT.values);
      Object.assign(warnings, sT.ok ? sT.warnings : {});
    }

    const rUser = userId
      ? await fetchScope({ scope: "user", user_id: userId })
      : await fetchScope({ scope: "user", identifier });
    if (rUser?.error) {
      if (!__isMissingTableError(rUser.error))
        console.warn("[appearance] user fetch failed:", rUser.error);
    } else {
      const userValue =
        rUser?.data?.value && typeof rUser.data.value === "object"
          ? rUser.data.value
          : {};
      const sU = sanitizeAppearanceOverrides(userValue);
      if (sU.ok) Object.assign(merged, sU.values);
      Object.assign(warnings, sU.ok ? sU.warnings : {});
    }

    __writeLs("global", merged);
    return { ok: true, theme: merged, cached: false, warnings };
  } catch (e) {
    console.warn(
      "[appearance] loadAppearanceThemeRemote failed, using empty theme:",
      e,
    );
    return { ok: true, theme: {}, cached: false, reason: "exception" };
  }
}

/**
 * Saves theme overrides for a scope (best-effort):
 * - always writes to localStorage
 * - DB only if app_config REST verfügbar (sonst Admin-API / cachedOnly)
 *
 * @param {Object} params
 * @param {"global"|"tenant"|"user"} [params.scope]
 * @param {Record<string,any>} params.value
 */
export async function saveAppearanceThemeRemote({
  scope = "global",
  value,
} = {}) {
  const src = value && typeof value === "object" ? value : {};
  const sanitized = sanitizeAppearanceOverrides(src);
  if (!sanitized.ok) return { ok: false, reason: "invalid-value" };

  __writeLs(String(scope || "global"), sanitized.values);

  if (isRestEndpointUnavailable(APP_CONFIG_SCHEMA_REST_KEY)) {
    return {
      ok: true,
      cachedOnly: true,
      missing: true,
      warnings: sanitized.warnings,
    };
  }

  const supabase = await getSupabaseClient();
  if (!supabase)
    return { ok: true, cachedOnly: true, warnings: sanitized.warnings };
  const db = __db(supabase);

  const { tenantId } = __getCtxHints();
  const identifier = __getIdentifier();

  let userId = null;
  try {
    const { data } = await supabase.auth.getUser();
    userId = data?.user?.id || null;
  } catch (_) {}

  const scopeStr = String(scope || "global");
  if (scopeStr === "tenant" && !tenantId) {
    return {
      ok: true,
      cachedOnly: true,
      reason: "missing-tenant",
      warnings: sanitized.warnings,
    };
  }
  const row = {
    scope: scopeStr,
    scope_id:
      scopeStr === "global"
        ? "global"
        : scopeStr === "tenant"
          ? String(tenantId || "")
          : userId
            ? String(userId)
            : String(identifier || ""),
    key: KEY_THEME,
    value: sanitized.values,
    tenant_id: scopeStr === "tenant" ? tenantId || null : null,
    user_id: scopeStr === "user" && userId ? userId : null,
    identifier: scopeStr === "user" && !userId ? identifier : null,
  };

  try {
    const { data, error } = await db
      .from(T_SETTINGS)
      .upsert(row, { onConflict: "scope,scope_id,key" })
      .select("updated_at")
      .limit(1);

    if (error) {
      if (
        isAppConfigSchemaUnavailableError(error) ||
        __isMissingTableError(error)
      ) {
        markRestEndpointUnavailable(APP_CONFIG_SCHEMA_REST_KEY);
        return {
          ok: true,
          cachedOnly: true,
          missing: true,
          warnings: sanitized.warnings,
        };
      }
      return {
        ok: false,
        reason: "db-error",
        error,
        warnings: sanitized.warnings,
      };
    }
    const saved = __firstRow(data);
    return {
      ok: true,
      updatedAt: saved?.updated_at || null,
      warnings: sanitized.warnings,
    };
  } catch (e) {
    return {
      ok: true,
      cachedOnly: true,
      reason: "exception",
      warnings: sanitized.warnings,
    };
  }
}

export function getAppearanceTokenDefinitions() {
  return APPEARANCE_TOKENS_V1.slice();
}
